
import React from 'react';
import StyledButton from './ui/StyledButton';
import { HeroProps } from './hero/HeroTypes';
import HeroBackground from './hero/HeroBackground';
import HeroTrustBadges from './hero/HeroTrustBadges';
import HeroUrgencyMessage from './hero/HeroUrgencyMessage';
import HeroButtons from './hero/HeroButtons';

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  buttonText,
  buttonLink,
  onButtonClick,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonIcon,
  backgroundImage,
  backgroundAlt,
  contentPosition = 'center',
  height = 'large',
  overlay = false, // Default to false to remove overlays
  overlayOpacity = 'medium',
  imageStyle = 'natural', // Changed default to natural for cleaner look
  className = '',
  showTrustBadges = false,
  showUrgencyMessage = false,
  urgencyMessage,
  trustBadges,
  imagePosition = 'center',
  children,
}) => {
  const handleButtonClick = () => {
    if (buttonLink && buttonLink.startsWith('#')) {
      // Handle anchor links
      const element = document.querySelector(buttonLink);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (onButtonClick) {
      onButtonClick();
    }
  };

  return (
    <section 
      className={`relative w-full flex items-center justify-center ${className}`}
      style={{
        minHeight: height === 'full' ? '100vh' : height === 'large' ? '80vh' : height === 'medium' ? '60vh' : '40vh',
      }}
      role="region"
      aria-label="Hero section"
    >
      {/* Background Image without Overlay by default */}
      <HeroBackground
        backgroundImage={backgroundImage}
        descriptiveAlt={backgroundAlt}
        overlay={overlay}
        overlayOpacity={overlayOpacity as 'light' | 'medium' | 'heavy'}
        imageStyle={imageStyle as 'darken' | 'natural' | 'brighten' | 'darker' | 'blur'}
        imagePosition={imagePosition}
        bgColorClass=""
      />
      
      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div 
          className={`
            flex flex-col items-center text-white text-center max-w-4xl mx-auto px-4
            ${contentPosition === 'center' ? 'justify-center' : ''}
            ${contentPosition === 'top' ? 'justify-start pt-16 md:pt-24' : ''}
            ${contentPosition === 'bottom' ? 'justify-end pb-16 md:pb-24' : ''}
          `}
        >
          {title && (
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-montserrat font-bold mb-4 md:mb-6 text-shadow-lg">
              {title}
            </h1>
          )}
          
          {subtitle && (
            <p className="text-xl md:text-2xl mb-6 md:mb-8 max-w-2xl font-opensans text-shadow-md">
              {subtitle}
            </p>
          )}

          {/* Children content if provided */}
          {children}
          
          {/* Button(s) */}
          {(buttonText || secondaryButtonText) && (
            <HeroButtons 
              buttonText={buttonText}
              buttonLink={buttonLink}
              onButtonClick={handleButtonClick}
              secondaryButtonText={secondaryButtonText}
              secondaryButtonLink={secondaryButtonLink}
              secondaryButtonIcon={secondaryButtonIcon}
              contentPosition={contentPosition as 'center' | 'left' | 'right'}
            />
          )}
          
          {/* Trust Badges */}
          {showTrustBadges && <HeroTrustBadges trustBadges={trustBadges} />}
          
          {/* Urgency Message */}
          {showUrgencyMessage && <HeroUrgencyMessage message={urgencyMessage} />}
        </div>
      </div>
    </section>
  );
};

export default Hero;
